import { NavLink, useNavigate } from "react-router-dom";
import {
  Gauge, Factory, Briefcase, LayoutDashboard as LayoutIcon, ClipboardCheck,
  AlertTriangle as Warning, Building2 as Buildings, BarChart3 as ChartBar,
  Lightbulb, LogOut as SignOut, Upload, Calculator, Wallet,
  Users as UsersIcon, ShieldCheck, Settings, Bell, Crown, ClipboardList, CalendarRange,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { canViewAny, hasPerm } from "../../lib/permissions";

const items = [
  { to: "/",                   label: "Overview",              Icon: Gauge,           module: "overview",         end: true },
  { to: "/departments",        label: "Departments",           Icon: Factory,         module: "departments" },
  { to: "/jobs",               label: "Jobs",                  Icon: Briefcase,       module: "jobs" },
  { to: "/gm-view",            label: "GM View",               Icon: Crown,           module: "gm_view" },
  { to: "/pm-view",            label: "PM View",               Icon: ClipboardList,   module: "pm_view" },
  { to: "/builder",            label: "Builder",               Icon: LayoutIcon,      module: "insights" },
  { to: "/qc-pipeline",        label: "QC Pipeline",           Icon: ClipboardCheck,  module: "qc_pipeline" },
  { to: "/ncr",                label: "NCR Dashboard",         Icon: Warning,         module: "ncr_dashboard" },
  { to: "/qc-by-plant",        label: "QC by Plant",           Icon: Buildings,       module: "qc_by_plant" },
  { to: "/look-ahead",         label: "Look Ahead",            Icon: CalendarRange,   module: "look_ahead" },
  { to: "/completion-rates",   label: "Completion Rates",      Icon: ChartBar,        module: "completion_rates" },
  { to: "/forecasting",        label: "Job Forecasting Tool",  Icon: Calculator,      module: "job_forecasting" },
  { to: "/labor-estimation",   label: "Labor Estimation Tool", Icon: Wallet,          module: "labor_estimation" },
  { to: "/insights",           label: "Insights",              Icon: Lightbulb,       module: "insights" },
  { to: "/notifications",      label: "Notifications",         Icon: Bell,            module: "ncr_alerts",  action: "subscribe" },
  { to: "/upload",             label: "Upload Data",           Icon: Upload,          module: "upload_data", action: "upload" },
];

const adminItems = [
  { to: "/users",         label: "Users",         Icon: UsersIcon,    module: "users" },
  { to: "/roles",         label: "Roles",         Icon: ShieldCheck,  module: "roles" },
  { to: "/configuration", label: "Configuration", Icon: Settings,     module: "configuration" },
];

function isVisible(user, item) {
  if (item.action) return hasPerm(user, item.module, item.action);
  return canViewAny(user, item.module);
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visibleMain = items.filter((it) => isVisible(user, it));
  const visibleAdmin = adminItems.filter((it) => isVisible(user, it));

  return (
    <aside
      data-testid="app-sidebar"
      className="fixed left-0 top-0 h-screen w-64 bg-[#12141D] border-r border-border flex flex-col z-40"
    >
      <div className="px-5 py-5 border-b border-border">
        <div className="font-heading font-bold text-[15px] tracking-tight">
          UDI<span className="text-[#00D4FF]">/OPS</span>
        </div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mt-1">Unlimited Designs<br/>Production Dashboard</div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {visibleMain.map(({ to, label, Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-all mb-0.5 ${isActive ? "bg-[#1A1C28] border-[#00D4FF] text-white" : "border-transparent text-[#94A3B8] hover:text-white hover:bg-[#1A1C28]/60 hover:border-[#00D4FF]/40"}`
            }
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}

        {visibleAdmin.length > 0 && (
          <div className="mt-4 pt-3 border-t border-border/60">
            <div className="px-3 text-[9px] tracking-[0.3em] uppercase text-muted-foreground mb-1.5">Admin</div>
            {visibleAdmin.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-all mb-0.5 ${isActive ? "bg-[#1A1C28] border-[#00D4FF] text-white" : "border-transparent text-[#94A3B8] hover:text-white hover:bg-[#1A1C28]/60 hover:border-[#00D4FF]/40"}`
                }
              >
                <Icon size={18} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      <div className="border-t border-border px-4 py-3 flex items-center gap-3">
        <div className="h-8 w-8 bg-[#1A1C28] border border-border flex items-center justify-center text-[11px] font-mono text-[#00D4FF]">
          {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs truncate text-white">{user?.name || "User"}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{user?.role_name || user?.role || "-"}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="text-muted-foreground hover:text-[#00D4FF] transition-colors"
          title="Logout"
        >
          <SignOut size={18} />
        </button>
      </div>
    </aside>
  );
}
